import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Customer } from '@ca/common/types'
import Line from '@ui/elements/Line/Line'
import SafePressable from '@ui/buttons/SafePressable'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { colors } from '@ui/colors'
import UserContactState from '@ui/tiles/TileUser/UserContactState'
import CustomerHeader from './CustomerHeader'
import { StatusProps } from '@ui/elements/Status/types'
import Gradient from '@ui/effects/Gradient'
import Border from '@ui/effects/Border'

interface Props {
  data: Customer & { id?: number }
  badges?: StatusProps[]
  onPress?: () => void
}

export const borderRadius = 24

export default function CustomerCard({ data, badges, onPress }: Props) {
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const { t } = useTranslation(['countries'])

  const { email, mobile, phone, email_state, phone_state, address } = data

  const countryName = address?.country_code
    ? t(`countries:${address.country_code.toLowerCase()}`)
    : undefined

  const stylePressedBackground = useAnimatedStyle(() => ({
    opacity: interpolate(animation.value, [0, 1], [0, 1]),
  }))

  const hasAddress = address?.city || address?.zip
  const hasContacts = email || mobile || phone
  const hasPhone = mobile || phone

  return (
    <SafePressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={!onPress}
    >
      <Animated.View
        className="relative inset-0 overflow-hidden"
        style={[{ borderRadius }]}
      >
        <Gradient
          colors={[colors.white0, colors.white0]}
          start={[0, 0.5]}
          end={[1, 0.5]}
          style={[{ borderRadius }]}
        />
        <CustomerHeader data={data} badges={badges} />
        <Gradient
          colors={[colors.text10, colors.text5]}
          start={[0, 0.5]}
          end={[1, 0.5]}
          style={[stylePressedBackground, { borderRadius }]}
        />

        {hasAddress && (
          <View>
            <Line />
            <View className="h-12 flex-row">
              <UserContactState
                type="address"
                data={[address?.zip, address?.city, countryName]
                  .filter(Boolean)
                  .join(', ')}
              />
            </View>
          </View>
        )}

        {hasContacts && (
          <View>
            <Line />
            <View className="h-12 flex-row">
              <View className="flex-1">
                <UserContactState
                  type="email"
                  data={email}
                  state={email_state}
                />
              </View>
              {hasPhone && (
                <View className="flex-1 flex-row">
                  <View className="w-px bg-black/10" />
                  <View className="flex-1">
                    <UserContactState
                      type="phone"
                      data={mobile ?? phone}
                      state={mobile ? phone_state : undefined}
                    />
                  </View>
                </View>
              )}
            </View>
          </View>
        )}

        <Border
          style={{ borderRadius, borderWidth: 1, borderColor: colors.black10 }}
        />
      </Animated.View>
    </SafePressable>
  )
}
